import { useState, useRef, useCallback, useEffect } from 'react';

export type UseDropZoneResult = {
  isDragActive: boolean;
  isOver: boolean;
  dragEnterHandler: () => void;
  dragLeaveHandler: () => void;
  dragOverHandler: (event: React.DragEvent) => void;
  dropHandler: (event: React.DragEvent) => void;
};

const containsFiles = (event: DragEvent): boolean =>
  !!(event.dataTransfer?.types ?? []).includes('Files');

export const useDropZone = (
  onFiles: (files: File[]) => void
): UseDropZoneResult => {
  const [isDragActive, setDragActive] = useState(false);
  const [isOver, setIsOver] = useState(false);
  const pageCounterRef = useRef(0);

  useEffect(() => {
    const handleDragEnter = (event: DragEvent) => {
      if (!containsFiles(event)) {
        return;
      }
      event.preventDefault();
      pageCounterRef.current++;
      if (pageCounterRef.current === 1) {
        setDragActive(true);
      }
    };

    const handleDragOver = (event: DragEvent) => {
      if (!containsFiles(event)) {
        return;
      }
      event.preventDefault();
    };

    const handleDragLeave = () => {
      pageCounterRef.current--;
      if (pageCounterRef.current === 0) {
        setDragActive(false);
        setIsOver(false);
      }

      if (pageCounterRef.current < 0) {
        pageCounterRef.current = 0;
      }
    };

    const handleDrop = (event: DragEvent) => {
      event.preventDefault();
      pageCounterRef.current = 0;
      setDragActive(false);
      setIsOver(false);
    };

    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('drop', handleDrop);

    return () => {
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('drop', handleDrop);
    };
  }, []);

  const dragEnterHandler = useCallback(() => setIsOver(true), []);
  const dragLeaveHandler = useCallback(() => setIsOver(false), []);
  const dragOverHandler = useCallback((event: React.DragEvent) => {
    event.preventDefault();
  }, []);
  const dropHandler = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const files = Array.from(event.dataTransfer.files);
      if (files.length > 0) {
        onFiles(files);
      }
    },
    [onFiles]
  );

  return {
    isDragActive,
    isOver,
    dragEnterHandler,
    dragLeaveHandler,
    dragOverHandler,
    dropHandler
  };
};
